/**
 * API Client with Interceptors
 * Unified API configuration with base URL and global headers using Axios
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from "axios";
import Cookies from "js-cookie";
import { shouldOmitDomainHeader } from "../utils/externalNavigation";
import {
  clearPersistedDomain,
  loadPersistedDomain,
  persistDomain,
} from "../utils/domainPersistence";
import { envConfig } from "../config/env";
import { isChatbotDetailSkipDomainActive } from "../utils/chatbotDetailApiSession";
import {
  getAcTenantId,
  isAutoTaggingApiUrl,
  X_TENANT_ID_HEADER,
} from "../utils/acTenantId";

function serializeParams(params: Record<string, any> | undefined): string {
  if (!params) return "";
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === undefined || item === null) continue;
        usp.append(key, String(item));
      }
      continue;
    }
    usp.append(key, String(value));
  }
  return usp.toString();
}

/**
 * API Configuration
 */
const API_CONFIG = {
  baseURL:
    envConfig.apiBaseUrl ||
    "https://5qrqnojf3i.execute-api.us-east-1.amazonaws.com",
  // Service prefix - using default value, can be customized via envConfig if needed
  servicePrefix: "/gri-service",
  timeout: 0, // 0 means no timeout limit
};

/**
 * Sync authentication from cookies to localStorage
 * This ensures localStorage always has the latest token from cookies
 */
function syncAuthFromCookies(): void {
  // 1. Try to get ACCESS_TOKEN from cookie
  const accessToken = Cookies.get("ACCESS_TOKEN");

  if (accessToken) {
    // Store token in localStorage with Bearer prefix
    localStorage.setItem("Authorization", `Bearer ${accessToken}`);
  }
}

/**
 * Get authentication token from cookies or localStorage
 * Priority: LoginToken (cookie) > ACCESS_TOKEN (cookie) > localStorage
 */
function getAuthToken(): string {
  // Try LoginToken first
  let token = Cookies.get("LoginToken");

  // Fallback to ACCESS_TOKEN
  if (!token) {
    token = Cookies.get("ACCESS_TOKEN");
  }

  // Fallback to localStorage
  if (!token) {
    const storedAuth = localStorage.getItem("Authorization");
    if (storedAuth && storedAuth.startsWith("Bearer ")) {
      token = storedAuth.substring(7); // Remove "Bearer " prefix
    } else {
      token = localStorage.getItem("token") || "";
    }
  }

  return token;
}

export function getAuthorizationHeader(): string | undefined {
  syncAuthFromCookies();
  const storedAuth = localStorage.getItem("Authorization");
  if (storedAuth && storedAuth.startsWith("Bearer ")) return storedAuth;
  const raw = getAuthToken();
  return raw ? `Bearer ${raw}` : undefined;
}

/**
 * Get user ID from cookies or localStorage
 * Priority: USER_EMAIL (cookie) > EMAIL (cookie) > localStorage > default
 */
export function getUserId(): string {
  // Try USER_EMAIL first
  let userId: string | undefined = Cookies.get("USER_EMAIL");

  // Fallback to EMAIL
  if (!userId) {
    userId = Cookies.get("EMAIL");
  }

  // Fallback to localStorage
  if (!userId) {
    userId = localStorage.getItem("user-id") || undefined;
  }

  // Fallback to default value
  return userId || "test.user@example.com";
}

/**
 * Get tenant ID from cookies or localStorage
 * Priority: TENANT_ID (cookie) > USER_TENANT_ID (cookie) > localStorage > default
 */
export function getTenantId(): string {
  // Try TENANT_ID first
  let tenantId: string | undefined = Cookies.get("TENANT_ID");

  // Fallback to USER_TENANT_ID
  if (!tenantId) {
    tenantId = Cookies.get("USER_TENANT_ID");
  }

  // Fallback to localStorage
  if (!tenantId) {
    tenantId = localStorage.getItem("tenant-id") || undefined;
  }

  // TODO: Revert to "0" - temporary default for API testing
  return tenantId || "30581";
}

const DEFAULT_DOMAIN = "MPR";

/** Writes domain to sessionStorage (source for API `domain` header). */
export function setDomain(domain: string): void {
  persistDomain(domain);
}

/** Resets domain persistence for unit tests. */
export function resetDomainStateForTests(): void {
  clearPersistedDomain();
}

/** Domain for API header: sessionStorage value, else default. */
export function getDomain(): string {
  return loadPersistedDomain() ?? DEFAULT_DOMAIN;
}

// Sync auth from cookies on module load
syncAuthFromCookies();

/**
 * API Response Interface
 */
export interface ApiResponse<T = any> {
  status: string;
  code: number;
  message?: string | null;
  data?: T | null;
}

/**
 * Request Options
 */
interface RequestOptions extends Omit<AxiosRequestConfig, "params"> {
  params?: Record<string, any>;
  /** When true, omit the `domain` header so backend can resolve by regulation id globally */
  skipDomain?: boolean;
}

/**
 * Build complete URL with service prefix
 */
function buildUrl(endpoint: string): string {
  if (endpoint.startsWith("http")) {
    return endpoint;
  }

  // Check if baseURL already includes the service prefix
  const needsServicePrefix = !API_CONFIG.baseURL.includes(
    API_CONFIG.servicePrefix,
  );
  return needsServicePrefix
    ? `${API_CONFIG.baseURL}${API_CONFIG.servicePrefix}${endpoint}`
    : `${API_CONFIG.baseURL}${endpoint}`;
}

/**
 * Create axios instance
 */
const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  paramsSerializer: (params) => serializeParams(params as Record<string, any>),
  headers: {
    "Content-Type": "application/json",
    accept: "application/json",
  },
});

/**
 * Request Interceptor
 * Handles authentication headers and special URL processing
 */
axiosInstance.interceptors.request.use(
  (config) => {
    // Sync auth from cookies before each request
    syncAuthFromCookies();

    // Get authentication values from cookies/localStorage
    const token = getAuthToken();
    const fullUrl = config.url || "";
    const domain = getDomain();
    // Global search API must not receive domain header (backend contract).
    // Deep links (chatbot, newsletter, related items) use ?source=… — see docs/navigation/external-module-links.md
    const omitDomainHeader =
      fullUrl.includes("/global-search") ||
      shouldOmitDomainHeader() ||
      Boolean(
        (config as AxiosRequestConfig & { skipDomain?: boolean }).skipDomain,
      ) ||
      isChatbotDetailSkipDomainActive();
    // config.headers.Authorization = `Bearer eyJraWQiOiIzWDJlM1JaSGpMQU9CZXhLRTMzRkhNcGJscVdFZFp4ZCtqRXArSXpUNFc4PSIsImFsZyI6IlJTMjU2In0.eyJzdWIiOiJiNTZhYzE0Mi1jYzI0LTRlOTQtOTdhMC1iNmJlYTE5YTc0NzciLCJjb2duaXRvOmdyb3VwcyI6WyJ0ZW5hbnRfMzA1ODEiXSwiaXNzIjoiaHR0cHM6XC9cL2NvZ25pdG8taWRwLnVzLWVhc3QtMS5hbWF6b25hd3MuY29tXC91cy1lYXN0LTFfUnp4dkVhcDl0IiwiY2xpZW50X2lkIjoiMWtlOWg1ZmJpYWxtZ3VxaHVxZG9wamtjOHQiLCJvcmlnaW5fanRpIjoiYTg4ZDIwNDItZjY1Zi00MzVkLWFjN2YtMzg2MjRmMjY3NDA4IiwiZXZlbnRfaWQiOiIxZjMwMjdjNy03MmZlLTRiMjYtYmYxNy00YzVkYmMwZTIwNTEiLCJ0b2tlbl91c2UiOiJhY2Nlc3MiLCJzY29wZSI6ImF3cy5jb2duaXRvLnNpZ25pbi51c2VyLmFkbWluIiwiYXV0aF90aW1lIjoxNzczMDIzMDMzLCJleHAiOjE3NzMxMDk0MzMsImlhdCI6MTc3MzAyMzAzMywianRpIjoiNWZlOWQyNTQtYTgyZi00NjkyLTkxNjAtYmM1ODk3NzM0ODJlIiwidXNlcm5hbWUiOiIxMDAwMjM2NzEifQ.Ngui1PjO4h3i_uzqPpYdyeiHkVRzKmduy9zSxhH-DfuyS5146j5d2gY5NzGc0XfRBk0gMTjupCsLfwa7Dzf-ykwoCyDnHAvyZfZdMUOdFr8Rgrrkf0W4wl18ny-zGG2w3ZHUIQmtVM-VLDULpXBOgejlpdsoUkdXs0K05Z2fqmhWDuhOo6TNa1LoK4-FxEhEjvJ48uQNnJnn38yA8dG5QIz1aXerfHENc_Mi0SY7TCG5PjhXEeCn_qRfcnvQ5YSr1AdIefHfpq2Z0rGM63tWyUuMKitpR_MRP9gXGSPbttGtCAeyf-hex9MDoEmr5yrA4k_wE-LfFa7_dJf7lpsx1g`;
    // If URL doesn't contain s3.amazonaws.com, add auth headers
    if (!fullUrl.includes("s3.amazonaws.com")) {
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      // if (userId) {
      //   config.headers["user-id"] = userId;
      // }
      // if (tenantId) {
      //   config.headers["tenant-id"] = tenantId;
      // }
      if (domain && !omitDomainHeader) {
        config.headers["domain"] = domain;
      }

      // AI Plan / auto-tagging APIs require Assessment Center tenant id.
      if (isAutoTaggingApiUrl(fullUrl)) {
        const acTenantId = getAcTenantId();
        if (acTenantId) {
          config.headers[X_TENANT_ID_HEADER] = acTenantId;
        } else {
          delete config.headers[X_TENANT_ID_HEADER];
        }
      }
    }

    // Special handling for UAM menu resource endpoint
    if (fullUrl.includes("/common-uam/resources/menu/related-resource")) {
      config.headers["Content-Type"] = "application/json";
    }

    // Return modified config
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

/**
 * Parse backend 422 message when it is a stringified list of error objects
 * (e.g. Python repr style) and return a single string with each object's msg.
 */
function parseBundleTaskErrorMessages(message: string): string | null {
  if (!message || typeof message !== "string") return null;

  // Try JSON array of objects with msg
  try {
    const parsed = JSON.parse(message);
    if (Array.isArray(parsed) && parsed.length > 0) {
      const msgs = parsed
        .map((o: unknown) =>
          o && typeof o === "object" && "msg" in o
            ? (o as { msg: string }).msg
            : null,
        )
        .filter((m): m is string => typeof m === "string" && m.length > 0);
      if (msgs.length > 0) return msgs.join("\n");
    }
  } catch {
    // Not JSON, try Python literal style: [{'type': ..., 'msg': '...', ...}, ...]
  }

  // Python-style: extract each 'msg': '...' (allow '' as escaped quote in value)
  const msgRegex = /'msg':\s*'((?:[^']|'')*)'/g;
  const msgs: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = msgRegex.exec(message)) !== null) {
    msgs.push(m[1].replace(/''/g, "'"));
  }
  if (msgs.length > 0) return msgs.join("\n");
  return null;
}

/** Format 422 bundle-task error message (list-like or "no supported document types") */
function format422ErrorMessage(message: string): string | null {
  const parsedMsgs = parseBundleTaskErrorMessages(message);
  if (parsedMsgs) return parsedMsgs;

  if (!message.includes("no supported document types")) return null;

  const regulationsMatch = message.match(/regulations?: \[([^\]]+)\]/);
  const typesMatch = message.match(/types: \[([^\]]+)\]/);
  if (!regulationsMatch || !typesMatch) return null;

  const regulations = regulationsMatch[1].replace(/'/g, "").replace(/"/g, "");
  const types = typesMatch[1]
    .replace(/'/g, "")
    .replace(/"/g, "")
    .split(", ")
    .join(" or ")
    .toUpperCase();

  return `Cannot start assessment: The selected regulation(s) (${regulations}) do not have any documents in supported formats (${types}). Please upload a ${types} document first.`;
}

/** Get user-friendly fallback message when backend provides none */
function getDefaultErrorMessage(status: number, statusText?: string): string {
  const fallbacks: Record<number, string> = {
    400: "Bad request. Please check your input and try again.",
    401: "Authentication required. Please log in and try again.",
    403: "Access denied. You don't have permission to perform this action.",
    404: "Resource not found. The requested item may have been deleted or moved.",
    422: "Validation error. Please check your input and try again.",
    429: "Too many requests. Please wait a moment and try again.",
    500: "Server error. Please try again later.",
    502: "Server error. Please try again later.",
    503: "Server error. Please try again later.",
    504: "Server error. Please try again later.",
  };
  return (
    fallbacks[status] ??
    statusText ??
    "An unexpected error occurred. Please try again."
  );
}

function collectErrorMessagesFromPayload(data: unknown): string[] {
  if (!data || typeof data !== "object") return [];
  const record = data as {
    errors?: Array<{ message?: string }>;
    data?: { errors?: Array<{ message?: string }> };
  };
  const lists = [record.errors, record.data?.errors];
  for (const errors of lists) {
    if (!Array.isArray(errors) || errors.length === 0) continue;
    const messages = errors
      .map((item) => item.message?.trim())
      .filter((msg): msg is string => Boolean(msg));
    if (messages.length > 0) return messages;
  }
  return [];
}

/** Build error message from API response */
function buildResponseErrorMessage(response: AxiosError["response"]): string {
  const data = response?.data as
    | {
        message?: string;
        detail?: Array<{ msg?: string }>;
        errors?: Array<{ message?: string; field?: string }>;
        data?: { errors?: Array<{ message?: string; field?: string }> };
      }
    | undefined;
  const backendMessage = data?.message;

  const nestedErrorMessages = collectErrorMessagesFromPayload(data);
  if (nestedErrorMessages.length > 0) {
    return nestedErrorMessages.join("\n");
  }

  if (response?.status === 422 && Array.isArray(data?.detail)) {
    const detailMessages = data.detail
      .map((item) => item.msg)
      .filter((msg): msg is string => Boolean(msg));
    if (detailMessages.length > 0) return detailMessages.join("\n");
  }

  if (backendMessage && response?.status === 422) {
    const formatted = format422ErrorMessage(backendMessage);
    if (formatted) return formatted;
  }

  if (backendMessage) return backendMessage;

  return getDefaultErrorMessage(response?.status ?? 0, response?.statusText);
}

/**
 * Response Interceptor
 * Handles error formatting and response parsing
 */
axiosInstance.interceptors.response.use(
  (response) => response.data,
  (error: AxiosError<any>) => {
    const response = error.response;

    if (response) {
      console.error("API Error:", {
        url: response.config?.url,
        status: response.status,
        statusText: response.statusText,
        data: response.data,
      });
      const errorMessage = buildResponseErrorMessage(response);
      const err = new Error(errorMessage) as Error & {
        httpStatus?: number;
        responseData?: unknown;
      };
      err.httpStatus = response.status;
      err.responseData = response.data;
      return Promise.reject(err);
    }

    if (error.code === "ECONNABORTED") {
      return Promise.reject(
        new Error(`Request timeout after ${API_CONFIG.timeout}ms`),
      );
    }

    if (error.message === "Network Error") {
      return Promise.reject(
        new Error("Network error. Please check your connection and try again."),
      );
    }

    return Promise.reject(new Error(error.message || "Unknown error occurred"));
  },
);

/**
 * API Request wrapper
 */
async function request<T = any>(
  endpoint: string,
  options: RequestOptions = {},
): Promise<ApiResponse<T>> {
  const url = buildUrl(endpoint);

  const config = {
    ...options,
    url,
  } as AxiosRequestConfig & { skipDomain?: boolean };

  return axiosInstance.request<any, ApiResponse<T>>(config);
}

/**
 * API Client
 */
export const apiClient = {
  /**
   * GET request
   */
  get: <T = any>(
    endpoint: string,
    params?: Record<string, any>,
    options?: Omit<RequestOptions, "method" | "params">,
  ) => request<T>(endpoint, { method: "GET", params, ...options }),

  /**
   * POST request
   */
  post: <T = any>(endpoint: string, data?: any, options?: RequestOptions) =>
    request<T>(endpoint, {
      method: "POST",
      data,
      ...options,
    }),

  /**
   * PUT request
   */
  put: <T = any>(endpoint: string, data?: any, options?: RequestOptions) =>
    request<T>(endpoint, {
      method: "PUT",
      data,
      ...options,
    }),

  /**
   * DELETE request
   */
  delete: <T = any>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { method: "DELETE", ...options }),

  /**
   * PATCH request
   */
  patch: <T = any>(endpoint: string, data?: any, options?: RequestOptions) =>
    request<T>(endpoint, {
      method: "PATCH",
      data,
      ...options,
    }),

  /**
   * Custom request
   */
  request,

  /**
   * Get base URL
   */
  getBaseURL: () => API_CONFIG.baseURL,

  /**
   * Get default headers
   */
  getHeaders: () => ({ ...axiosInstance.defaults.headers.common }),

  /**
   * Update global headers
   */
  setHeaders: (headers: Record<string, string>) => {
    Object.assign(axiosInstance.defaults.headers.common, headers);
  },

  /**
   * Update specific header
   */
  setHeader: (key: string, value: string) => {
    axiosInstance.defaults.headers.common[key] = value;
  },

  /**
   * Get axios instance (for advanced usage)
   */
  getInstance: () => axiosInstance,

  /**
   * Manually sync authentication from cookies to localStorage
   */
  syncAuth: syncAuthFromCookies,

  /**
   * Get current auth values (for debugging)
   */
  getAuthInfo: () => ({
    token: getAuthToken(),
    userId: getUserId(),
    tenantId: getTenantId(),
  }),

  /**
   * Set domain for API requests
   */
  setDomain,

  /**
   * Get current domain value
   */
  getDomain,
};

/**
 * Export axios instance for direct usage
 */
export { axiosInstance };

/**
 * Export default
 */
export default apiClient;
